import { useState } from 'react';
import {
  Input,
  Form,
  Cascader,
  Select,
  Col,
  Row,
  Collapse
} from '@didi-pebble/pebble-design-react';
import { i18nTranslate } from '@didi/i18n-plugin';

import { ConditionType } from '@/config';
import { rangePickerPresets, toTrim, useIsMobile } from '@/utils';

import ResponsiveRangePicker from '../responsiveRangePicker';
import SearchComp from '../searchComp';
import styles from './index.module.less';

import type { DefaultOptionType } from 'antd/es/cascader';

const { Panel } = Collapse;

const FilterList = (props: any) => {
  const {
    form,
    conditions,
    onFieldsChange = () => {},
    initialValues = {},
    onValuesChange = () => {},
    formStyle = {},
    needResponsive = true, // 是否需要响应式,如果为false，传入的style.width起效
    responsiveMap = {
      // 响应式布局
      mobile: 1, // 手机端一行1个
      tablet: 2, // 平板端一行2个
      pc: 3 // PC端一行3个
    }
  } = props;
  const isMobile = useIsMobile();
  const [collapseKeys, setCollapseKeys] = useState<string | string[]>(['1']);

  const defaultFilter = (inputValue: string, path: DefaultOptionType[]) =>
    path.some(option => {
      return ((option?.label ?? '') as string).toLowerCase().includes(inputValue.toLowerCase());
    });

  const defaultSelectFilter = (input: any, option: any) =>
    ((option?.label ?? '') as string).toLowerCase().includes(input.toLowerCase());

  const getRangeDate = (index: any, rangeDateProps: any = {}) => {
    const {
      name = 'date',
      style = { width: 300 },
      allowClear = false,
      presets = rangePickerPresets(),
      presetsUseBtn = false,
      showTime = false,
      format = 'DD-MM-YYYY'
    } = rangeDateProps;

    return (
      <Form.Item name={name} key={index} style={isMobile ? { marginRight: 0 } : {}}>
        <ResponsiveRangePicker
          style={{ ...style, width: needResponsive ? '100%' : style?.width }}
          showTime={showTime}
          allowClear={allowClear}
          presets={presets}
          presetsUseBtn={presetsUseBtn}
          format={format}
        />
      </Form.Item>
    );
  };

  const getInput = (index: any, inputProps: any = {}) => {
    const {
      name = 'input',
      placeholder = i18nTranslate('Internal_Tools_B_upfK_Vzsc'), // '请输入'
      allowClear = true,
      style = { width: 300 }
    } = inputProps;

    return (
      <Form.Item
        name={name}
        normalize={value => {
          const trimmedStr = toTrim(value);

          return trimmedStr;
        }}
        key={index}
        style={isMobile ? { marginRight: 0 } : {}}
      >
        <Input
          placeholder={placeholder} // 'Applied person/email'
          allowClear={allowClear}
          style={{ ...style, width: needResponsive ? '100%' : style?.width }}
          autoComplete='off'
          size={isMobile ? 'large' : 'middle'}
        />
      </Form.Item>
    );
  };

  const getSearchWord = (index: any, searchWordProps: any = {}) => {
    const {
      name = 'searchWord',
      placeholder = i18nTranslate('Internal_Tools_B_upfK_Vzsc'), // '请输入'
      allowClear = true,
      style = { width: 300 },
      getValueFromEvent = (e: any) => {
        return e.target.value;
      }
    } = searchWordProps;

    return (
      <Form.Item name={name} key={index} style={isMobile ? { marginRight: 0 } : {}}>
        <SearchComp
          placeholder={placeholder} // 'Applied person/email'
          allowClear={allowClear}
          getValueFromEvent={getValueFromEvent}
          style={{ ...style, width: needResponsive ? '100%' : style?.width }}
          size={isMobile ? 'large' : 'middle'}
        />
      </Form.Item>
    );
  };

  const getMultiCascader = (index: any, cascaderProps: any = {}) => {
    const {
      name,
      placeholder = i18nTranslate('Internal_Tools_B_CieE_fzIW'), // '请选择'
      allowClear = true,
      dropdownMatchSelectWidth = true,
      style = { width: 230 },
      options,
      filter = defaultFilter
    } = cascaderProps;

    return (
      <Form.Item name={name} key={index} style={isMobile ? { marginRight: 0 } : {}}>
        <Cascader
          style={{ ...style, width: needResponsive ? '100%' : style?.width }}
          allowClear={allowClear}
          placeholder={placeholder} // 'Gas station Name'
          multiple
          maxTagCount={1}
          maxTagTextLength={8}
          showSearch={{ filter }}
          options={options}
          dropdownMatchSelectWidth={dropdownMatchSelectWidth}
          getPopupContainer={(triggerNode: any) => triggerNode.parentElement}
          size={isMobile ? 'large' : 'middle'}
        />
      </Form.Item>
    );
  };

  const getMultiSelect = (index: any, selectProps: any = {}) => {
    const {
      name,
      placeholder = i18nTranslate('Internal_Tools_B_CieE_fzIW'), // '请选择'
      showSearch = true,
      allowClear = true,
      style = { width: 230 },
      options,
      optionFilterProp = 'children',
      filter = defaultSelectFilter,
      dropdownMatchSelectWidth = true,
      maxTagCount = 'responsive',
      maxTagTextLength = 10
    } = selectProps;

    return (
      <Form.Item name={name} key={index} style={isMobile ? { marginRight: 0 } : {}}>
        <Select
          placeholder={placeholder}
          style={{ ...style, width: needResponsive ? '100%' : style?.width }}
          dropdownStyle={{ maxWidth: '90vw' }}
          showSearch={showSearch}
          allowClear={allowClear}
          options={options}
          optionFilterProp={optionFilterProp}
          filterOption={filter}
          dropdownMatchSelectWidth={dropdownMatchSelectWidth}
          mode='multiple'
          maxTagCount={maxTagCount}
          maxTagTextLength={maxTagTextLength}
          size={isMobile ? 'large' : 'middle'}
        />
      </Form.Item>
    );
  };

  const getSelect = (index: any, selectProps: any = {}) => {
    const {
      name,
      placeholder = i18nTranslate('Internal_Tools_B_CieE_fzIW'), // '请选择'
      showSearch = true,
      allowClear = true,
      style = { width: 230 },
      options,
      optionFilterProp = 'children',
      filter = defaultSelectFilter,
      dropdownMatchSelectWidth = true
    } = selectProps;

    return (
      <Form.Item name={name} key={index} style={isMobile ? { marginRight: 0 } : {}}>
        <Select
          placeholder={placeholder}
          style={{ ...style, width: needResponsive ? '100%' : style?.width }}
          showSearch={showSearch}
          allowClear={allowClear}
          options={options}
          optionFilterProp={optionFilterProp}
          filterOption={filter}
          dropdownMatchSelectWidth={dropdownMatchSelectWidth}
          size={isMobile ? 'large' : 'middle'}
        />
      </Form.Item>
    );
  };

  const conditionMap: any = {
    [ConditionType.RangeDate]: getRangeDate,
    [ConditionType.SearchWord]: getSearchWord,
    [ConditionType.MultiCascader]: getMultiCascader,
    [ConditionType.MultiSelect]: getMultiSelect,
    [ConditionType.Input]: getInput,
    [ConditionType.Select]: getSelect
  };

  const getCollapse = (children: any) => {
    return (
      <Collapse
        defaultActiveKey={'1'}
        ghost
        onChange={(key: string | string[]) => {
          setCollapseKeys(key);
        }}
      >
        <Panel
          header={
            collapseKeys.length > 0
              ? `${i18nTranslate('Internal_Tools_2_dhrn_UOGT') || '折叠筛选条件'}`
              : `${i18nTranslate('Internal_Tools_2_XHWq_gRnu') || '展开筛选条件'}`
          }
          key='1'
          style={{ marginTop: 15 }}
        >
          {children}
        </Panel>
      </Collapse>
    );
  };

  const renderForm = () => {
    const colParams = needResponsive
      ? {
          xs: 24 / (responsiveMap.mobile || 1),
          sm: 24 / (responsiveMap.mobile || 1),
          md: 24 / (responsiveMap.tablet || 2),
          lg: 24 / (responsiveMap.pc || 3)
        }
      : {};

    return (
      <Form
        form={form}
        layout={'inline'}
        onFieldsChange={onFieldsChange}
        initialValues={initialValues}
        onValuesChange={onValuesChange}
        style={formStyle}
      >
        <Row style={{ width: '100%' }}>
          {conditions.map((item: any, index: number) => (
            <Col {...colParams} key={index}>
              {conditionMap[item.key](index, item.params)}
            </Col>
          ))}
        </Row>
      </Form>
    );
  };

  return (
    <div className={styles.collapse_filter}>
      {needResponsive && isMobile && conditions.length > 1
        ? getCollapse(renderForm())
        : renderForm()}
    </div>
  );
};

export default FilterList;
